import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../services/api";

export default function HydrationPage() {
  const [todayData, setTodayData] = useState({ cups: 0, ml: 0, settings: { dailyGoalMl: 2000, cupSizeMl: 250 } });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [customMl, setCustomMl] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  // Settings states
  const [editSettings, setEditSettings] = useState(false);
  const [dailyGoalMl, setDailyGoalMl] = useState(2000);
  const [cupSizeMl, setCupSizeMl] = useState(250);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchData = async () => {
    try {
      const [tRes, hRes, sRes] = await Promise.all([
        api.get("/water/today").catch(() => null),
        api.get("/water/logs").catch(() => null),
        api.get("/water/settings").catch(() => null),
      ]);

      if (tRes?.data) setTodayData(tRes.data);
      if (hRes?.data?.items) setHistory(hRes.data.items);
      if (sRes?.data?.settings) {
        setDailyGoalMl(sRes.data.settings.dailyGoalMl || 2000);
        setCupSizeMl(sRes.data.settings.cupSizeMl || 250);
      }
    } catch {
      // sem ação
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddMl = async (mlToAdd) => {
    setSaving(true);
    setFeedback("");
    try {
      await api.post("/water", { amountMl: mlToAdd });
      setFeedback(`+${mlToAdd} ml adicionados com sucesso! 💧`);
      setCustomMl("");
      await fetchData();
    } catch {
      setFeedback("Erro ao registrar consumo. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/water/settings", {
        dailyGoalMl: Number(dailyGoalMl),
        cupSizeMl: Number(cupSizeMl),
      });
      setEditSettings(false);
      await fetchData();
    } catch {
      alert("Erro ao salvar configurações.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm("Deseja realmente remover este registro de água?")) return;
    try {
      await api.delete(`/water/logs/${id}`);
      await fetchData();
    } catch {
      alert("Não foi possível excluir o registro.");
    }
  };

  // History toggle state
  const [historyExpanded, setHistoryExpanded] = useState(true);

  const totalMl = todayData.ml || 0;
  const goalMl = todayData.settings?.dailyGoalMl || dailyGoalMl || 2000;
  const cupSize = todayData.settings?.cupSizeMl || cupSizeMl || 250;
  const currentCups = Math.floor(totalMl / cupSize);
  const targetCups = Math.ceil(goalMl / cupSize);
  const percent = Math.min(100, Math.round((totalMl / goalMl) * 100));

  return (
    <div className="space-y-4 pb-24 font-sans">
      {/* BOTÃO VOLTAR PARA A HOME */}
      <div className="flex items-center justify-between pb-1">
        <Link
          to="/app/home"
          className="inline-flex items-center gap-1.5 text-xs font-extrabold text-[#6C54D8] hover:text-[#5A45BF] bg-purple-100/80 hover:bg-purple-200/80 px-3.5 py-2 rounded-2xl border border-purple-200/60 transition-all cursor-pointer shadow-2xs"
        >
          <span className="text-sm">←</span>
          <span>Voltar</span>
        </Link>
      </div>

      {/* 1. ESTADO ATUAL & PROGRESSO DE HOJE */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#6D6680] block">Consumo de Hoje</span>
            <h2 className="text-2xl font-black text-[#24152F] font-serif mt-0.5">
              {currentCups} de {targetCups} copos
            </h2>
            <p className="text-xs font-semibold text-purple-700 mt-0.5">
              {totalMl.toLocaleString("pt-BR")} ml de {goalMl.toLocaleString("pt-BR")} ml
            </p>
          </div>

          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600 font-black text-base shadow-xs border border-blue-200">
            {percent}%
          </div>
        </div>

        {/* BARRA DE COPOS ILUSTRATIVOS */}
        <div className="w-full max-w-full flex items-center gap-1.5 pt-2 overflow-x-auto pb-1 no-scrollbar">
          {Array.from({ length: Math.max(targetCups, 8) }).map((_, idx) => {
            const isFilled = idx < currentCups;
            return (
              <div
                key={idx}
                className={`h-8 w-6 rounded-md border flex items-center justify-center text-xs transition-all ${
                  isFilled
                    ? "bg-[#6C54D8] border-[#6C54D8] text-white shadow-xs"
                    : "border-dashed border-purple-200 bg-purple-50/40 text-transparent"
                }`}
              >
                💧
              </div>
            );
          })}
        </div>

        {/* BARRA CONTINUA DE PROGRESSO */}
        <div className="w-full overflow-hidden rounded-full bg-purple-100/60 p-0.5 border border-purple-100">
          <div
            className="h-3 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 transition-all duration-700 ease-out shadow-xs"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* 2. REGISTRAR CONSUMO RÁPIDO */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3.5">
        <h3 className="text-sm font-bold text-[#24152F]">Registrar Consumo</h3>

        {feedback && (
          <div className="p-3 rounded-2xl bg-purple-50 border border-purple-200 text-xs font-bold text-purple-800">
            {feedback}
          </div>
        )}

        {/* BOTÃO PRINCIPAL: + 1 COPO */}
        <button
          type="button"
          onClick={() => handleAddMl(cupSize)}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#6C54D8] to-[#5A45BF] hover:opacity-95 active:scale-98 text-white font-black text-sm uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
        >
          <span className="text-xl">💧</span>
          <span>+ 1 Copo ({cupSize} ml)</span>
        </button>

        {/* ATALHOS DE VOLUMES */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          {[150, 300, 500].map((mlVal) => (
            <button
              key={mlVal}
              type="button"
              onClick={() => handleAddMl(mlVal)}
              disabled={saving}
              className="py-2.5 px-2 rounded-2xl bg-purple-100/80 hover:bg-purple-200/80 text-[#6C54D8] text-xs font-extrabold transition-all border border-purple-200/50 cursor-pointer active:scale-95"
            >
              + {mlVal} ml
            </button>
          ))}
        </div>

        {/* CAMPO DE VALOR CUSTOMIZADO (RESPONSIVO: EMPILHA EM TELAS PEQUENAS, LADO A LADO EM LARGURAS SUFICIENTES) */}
        <div className="w-full max-w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 min-w-0">
          <input
            type="number"
            placeholder="Outro valor em ml (ex: 400)"
            value={customMl}
            onChange={(e) => setCustomMl(e.target.value)}
            className="input-base h-11 min-w-0 flex-1 w-full text-sm font-bold box-border"
          />
          <button
            type="button"
            onClick={() => customMl && handleAddMl(Number(customMl))}
            disabled={saving || !customMl}
            className="h-11 px-5 w-full sm:w-auto shrink-0 rounded-2xl bg-[#24152F] text-white text-xs font-extrabold cursor-pointer hover:bg-black transition-all disabled:opacity-40 shadow-xs"
          >
            Adicionar
          </button>
        </div>
      </div>

      {/* 3. MINHA META E CONFIGURAÇÕES */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#6D6680]">Minha Meta de Hidratação</h3>
          <button
            type="button"
            onClick={() => setEditSettings(!editSettings)}
            className="text-xs font-bold text-[#6C54D8] hover:underline cursor-pointer"
          >
            {editSettings ? "Cancelar" : "Alterar meta"}
          </button>
        </div>

        {editSettings ? (
          <form onSubmit={handleSaveSettings} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Meta diária (ml)</label>
                <input
                  type="number"
                  value={dailyGoalMl}
                  onChange={(e) => setDailyGoalMl(e.target.value)}
                  className="input-base h-11 w-full text-sm font-bold"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Tamanho do copo (ml)</label>
                <input
                  type="number"
                  value={cupSizeMl}
                  onChange={(e) => setCupSizeMl(e.target.value)}
                  className="input-base h-11 w-full text-sm font-bold"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={savingSettings}
              className="w-full h-11 rounded-2xl bg-[#6C54D8] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#5A45BF] transition-all cursor-pointer"
            >
              Salvar Configurações
            </button>
          </form>
        ) : (
          <div className="p-3.5 rounded-2xl bg-purple-50/60 border border-purple-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-[#24152F]">Meta diária: {goalMl.toLocaleString("pt-BR")} ml</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Tamanho do copo padrão: {cupSize} ml</p>
            </div>
            <span className="text-xl">🎯</span>
          </div>
        )}
      </div>

      {/* 4. HISTÓRICO DE CONSUMO */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-extrabold uppercase tracking-wider text-[#6D6680]">
            Histórico de Consumo
          </h3>
          <button
            type="button"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="text-xs font-bold text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer flex items-center gap-1"
          >
            {historyExpanded ? "Ocultar histórico" : `Exibir Histórico (${history.length})`}
          </button>
        </div>

        {historyExpanded && (
          <div className="space-y-3 pt-1">
            {history.length > 0 ? (
              history.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 rounded-2xl border border-purple-100 bg-white">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">💧</span>
                    <div>
                      <h4 className="text-xs font-bold text-[#24152F]">
                        {item.ml || item.cups * 250} ml ({item.cups || 1} {item.cups === 1 ? "copo" : "copos"})
                      </h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {new Date(item.loggedAt).toLocaleString("pt-BR")}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleDeleteLog(item.id)}
                    className="text-xs font-bold text-red-500 hover:text-red-700 cursor-pointer"
                  >
                    Excluir
                  </button>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 font-medium py-2">
                Nenhum consumo registrado recentemente.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
